import { logger } from "./logger";

/**
 * A way IN to the support assistant, for the screens that cannot import it.
 *
 * The caller is `components/Modals/ErrorModal` ("Ask the assistant about this"), which arrives
 * WITH a question and wants it typed out ready, and cannot hold a reference to the panel — the
 * panel is mounted once, at the top of the app (`components/SupportAssistant/SupportAssistantHost`),
 * while an `ErrorModal` is portalled from wherever it happens to be raised. (The launcher bubble,
 * `SupportLauncherButton`, is drawn by the panel itself since 2026-10-01 and opens it directly;
 * `showSupportAssistant` below is the no-seed form of the same press.)
 *
 * ⚠️ THE TWO ARE SEPARATE FUNCTIONS, NOT ONE WITH AN OPTIONAL SEED, AND THE DIFFERENCE IS
 * VISIBLE AT THE LISTENER. `openSupportAssistant` REPLACES what is in the composer, which is
 * right when somebody asked for this panel to be opened about a specific thing. The launcher
 * must not: a user who typed half a question, closed the panel and pressed the button again
 * would lose it. `showSupportAssistant` therefore delivers no seed at all rather than an empty
 * one, and the panel reads "no seed" as "change nothing".
 *
 * ⚠️ ONE LISTENER, NOT AN ARRAY — AND THAT IS A CHECK, NOT A LIMITATION.
 * The panel is a single mount by construction. If a second one ever registers, the app has two
 * chat panels, two threads, and a coin flip over which one a seeded question lands in. An array
 * would hide that by delivering to both; a single slot makes the second registration visible in
 * the log and leaves the most recent mount owning the bus. Fail loudly, not plausibly.
 *
 * ⚠️ `openSupportAssistant` RETURNS A BOOLEAN AND CALLERS MUST READ IT.
 * `false` means nothing is listening — the feature flag is off, or the panel is not mounted on
 * this screen. A caller that ignores it renders a button that does nothing when pressed, which
 * is strictly worse than no button: the user concludes the app is broken rather than that the
 * help simply is not there. `isSupportAssistantMounted()` is the same fact, asked before
 * rendering the control rather than after clicking it.
 */

export interface SupportAssistantSeed {
  /** Pre-filled into the composer. It is NOT sent — the user presses Send themselves. */
  question: string;

  /**
   * Context the user did not type and should not have to: an error message, a correlation id,
   * the screen they were on. Never shown in the composer; it travels with the escalation
   * report if the conversation ends up there.
   */
  diagnostics?: string;
}

/**
 * ⚠️ `seed` IS OPTIONAL, AND ABSENT IS A MEANING RATHER THAN A DEFAULT. It means "open the
 * panel and touch nothing else" — no composer, no diagnostics, no clearing of what is already
 * in the thread. The panel branches on it; do not substitute an empty seed here to avoid the
 * branch, because `{ question: "" }` is a request to BLANK the composer.
 */
export type SupportAssistantOpener = (seed?: SupportAssistantSeed) => void;

let listener: SupportAssistantOpener | null = null;

/**
 * Register the panel. Returns the unsubscribe function, so it drops straight out of a
 * `useEffect`.
 *
 * The unsubscribe clears the slot ONLY if it is still ours. Under React 18 StrictMode the
 * effect runs, cleans up, and runs again — and with an unconditional clear the second mount's
 * registration would be wiped by the first mount's cleanup, leaving the bus permanently empty.
 */
export function onSupportAssistantOpen(fn: SupportAssistantOpener): () => void {
  if (listener && listener !== fn) {
    logger.warn(
      "supportAssistantBus: a second listener registered. The support assistant is meant to be " +
        "mounted exactly once (SupportAssistantHost); the newest mount now owns the bus."
    );
  }

  listener = fn;

  return () => {
    if (listener === fn) listener = null;
  };
}

/**
 * Is the panel there to be opened?
 *
 * Ask this BEFORE rendering a control that would call `openSupportAssistant`. It is false
 * whenever the panel is not mounted, which includes every environment where the Key Vault flag
 * is off — the panel's own subscription effect checks the flag, precisely so this answer can be
 * trusted rather than merely optimistic.
 */
export function isSupportAssistantMounted(): boolean {
  return listener !== null;
}

/**
 * Open the panel with `seed` pre-filled.
 *
 * Returns `false` when nothing is listening and NOTHING HAPPENS. The caller is expected to have
 * asked `isSupportAssistantMounted()` first; this is the race-free backstop for the gap between
 * that question and the click.
 */
export function openSupportAssistant(seed: SupportAssistantSeed): boolean {
  return deliver(seed);
}

/**
 * Open the panel and nothing more — no question typed, no diagnostics, the thread as it was.
 *
 * This is what the header's launcher button does. It is the ordinary "I want to ask something"
 * press, where the user has not told anybody what about yet, so there is nothing to pre-fill and
 * a blank composer is the correct outcome rather than a missing one.
 *
 * Returns `false` on the same terms as `openSupportAssistant`, and callers must read it for the
 * same reason: a launcher that does nothing when pressed is worse than no launcher.
 */
export function showSupportAssistant(): boolean {
  return deliver(undefined);
}

function deliver(seed?: SupportAssistantSeed): boolean {
  if (!listener) return false;

  try {
    listener(seed);
    return true;
  } catch (error) {
    // A help affordance must never take down the screen that offered it.
    logger.warn("supportAssistantBus: the listener threw.", error);
    return false;
  }
}

/** Test seam. Drops the listener. */
export function __resetSupportAssistantBus(): void {
  listener = null;
}
